import {
  Button,
  IconButton,
  Input,
  Popover,
  Portal,
  Text,
  VStack,
  useFilter,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { FiSettings } from 'react-icons/fi';

export interface DocumentComposerModelPickerProps {
  value: string;
  onValueChange: (id: string) => void;
  options: string[];
  disabled?: boolean;
}

const MAX_VISIBLE_MODEL_RESULTS = 10;

/** When there are more matches than `max`, show the first `max` unless the selected id is past that window — then pin it with the next `max - 1` others. */
function takeVisibleLimited(
  filtered: string[],
  selectedId: string,
  max: number,
): string[] {
  if (filtered.length <= max) return filtered;
  const head = filtered.slice(0, max);
  if (head.includes(selectedId)) return head;
  if (filtered.includes(selectedId)) {
    const others = filtered.filter((id) => id !== selectedId);
    return [selectedId, ...others.slice(0, max - 1)];
  }
  return head;
}

/**
 * Gear popover to pick the OpenAI model for the next document edit prompt (session-only).
 */
export function DocumentComposerModelPicker({
  value,
  onValueChange,
  options,
  disabled,
}: DocumentComposerModelPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { contains } = useFilter({ sensitivity: 'base' });
  const filteredOptions = useMemo(() => {
    const q = query.trim();
    if (!q) return options;
    return options.filter((id) => contains(id, q));
  }, [options, query, contains]);

  const displayedOptions = useMemo(
    () => takeVisibleLimited(filteredOptions, value, MAX_VISIBLE_MODEL_RESULTS),
    [filteredOptions, value],
  );

  const truncatedCount = filteredOptions.length - displayedOptions.length;

  return (
    <Popover.Root
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
        if (!e.open) setQuery('');
      }}
      positioning={{ placement: 'top-end' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <IconButton
          type="button"
          variant="outline"
          size="sm"
          aria-label="Model for this prompt"
          title="Model for this prompt"
          disabled={disabled}
          style={{
            height: '100%',
            minHeight: '100%',
            alignSelf: 'stretch',
          }}
        >
          <FiSettings size={18} strokeWidth={2} aria-hidden />
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            p={2}
            minW="220px"
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={2} align="stretch">
              <Text fontSize="xs" color="fg.muted" px={1}>
                Model for this prompt
              </Text>
              <Input
                size="sm"
                placeholder="Search models…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
              {filteredOptions.length === 0 ? (
                <Text fontSize="xs" color="fg.muted" px={1} py={1}>
                  No matching models
                </Text>
              ) : (
                <>
                  {displayedOptions.map((id) => (
                    <Button
                      key={id}
                      size="xs"
                      variant={value === id ? 'solid' : 'ghost'}
                      colorScheme={value === id ? 'blue' : 'gray'}
                      justifyContent="flex-start"
                      fontWeight={value === id ? 'semibold' : 'normal'}
                      onClick={() => {
                        onValueChange(id);
                        setOpen(false);
                      }}
                    >
                      {id}
                    </Button>
                  ))}
                  {truncatedCount > 0 && (
                    <Text fontSize="xs" color="fg.muted" px={1} py={0.5}>
                      Showing {displayedOptions.length} of{' '}
                      {filteredOptions.length}. Refine search to see more.
                    </Text>
                  )}
                </>
              )}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
